import { NavLink } from "@/layouts/AppLayout/types";
import useCapabilities, {
  DOMAIN,
  useCanViewBeneficiariesBool,
  useCanViewGrpCommissionBool,
  useCanViewInteractionsBool,
  useHasRoles
} from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { usePersonProfileReadCapabilities } from "@/features/person/hooks/usePersonProfileReadCapabilities";
import { assignHotkeysToNavLinks, PERSON_PROFILE_SIDEBAR_RESERVED_HOTKEYS } from "@/lib/navLinkHotkeys";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { Path } from "@/router";

export const PERSON_NAV_ALL_PEOPLE_LABEL = "All People";

export function usePersonAdminToolsAccess() {
  const { loaded } = useCapabilities();
  const isPortalStaff = useHasRoles([ROLE.UNIVERSITY_STAFF, ROLE.PGA_MAGAZINE_STAFF]);

  return {
    loaded,
    canAccessAdminTools: loaded && !isPortalStaff,
    showAllPeopleNav: loaded && isPortalStaff
  };
}

export const usePersonNavLinks = (pgaId: string): Record<string, NavLink<Path>[]> => {
  const canViewInteractions = useCanViewInteractionsBool();
  const canViewBeneficiaries = useCanViewBeneficiariesBool();
  const { canAccessAdminTools, showAllPeopleNav } = usePersonAdminToolsAccess();
  const canViewGrpCommission = useCanViewGrpCommissionBool();
  const flags = useFlags();
  const canViewBenefits = canViewBeneficiaries || (!!flags["commission-grp-platform"] && canViewGrpCommission);
  const { canViewPersonEducation, showMembershipNav, showCareerNav, showEventsNav, showBillingNav } =
    usePersonProfileReadCapabilities();

  const personLinks = [
    {
      domain: DOMAIN.EVERYONE,
      label: "Person",
      link: {
        to: "/people/:pgaId/person",
        params: { pgaId }
      },
      icon: "assignment_ind"
    },
    showMembershipNav && {
      domain: DOMAIN.EVERYONE,
      label: "Membership",
      link: {
        to: "/people/:pgaId/membership",
        params: { pgaId }
      },
      icon: "badge"
    },
    canViewPersonEducation && {
      domain: DOMAIN.EVERYONE,
      label: "Education",
      link: {
        to: "/people/:pgaId/education",
        params: { pgaId }
      },
      icon: "school"
    },
    showCareerNav && {
      domain: DOMAIN.EVERYONE,
      label: "Career",
      link: {
        to: "/people/:pgaId/career",
        params: { pgaId }
      },
      icon: "business_center"
    },
    showEventsNav && {
      domain: DOMAIN.EVERYONE,
      label: "Events",
      link: {
        to: "/people/:pgaId/events",
        params: { pgaId }
      },
      icon: "today"
    },
    showBillingNav && {
      domain: DOMAIN.EVERYONE,
      label: "Billing",
      link: {
        to: "/people/:pgaId/billing",
        params: { pgaId }
      },
      icon: "card_membership"
    },
    canViewBenefits && {
      domain: DOMAIN.EVERYONE,
      label: "Benefits",
      link: {
        to: "/people/:pgaId/benefits",
        params: { pgaId }
      },
      icon: "volunteer_activism"
    },
    canViewInteractions && {
      domain: DOMAIN.EVERYONE,
      label: "Interactions",
      link: {
        to: "/people/:pgaId/interactions",
        params: { pgaId }
      },
      icon: "3p"
    },
    canAccessAdminTools && {
      domain: DOMAIN.EVERYONE,
      label: "Admin Tools",
      link: {
        to: "/people/:pgaId/admin-tools",
        params: { pgaId }
      },
      icon: "build"
    },
    showAllPeopleNav && {
      domain: DOMAIN.EVERYONE,
      label: PERSON_NAV_ALL_PEOPLE_LABEL,
      link: {
        to: "/people"
      },
      icon: "group",
      trailingIcon: "navigate_next",
      dividerBefore: true,
      externalLink: true
    }
  ].filter(Boolean) as NavLink<Path>[];

  return {
    "Manage Person": assignHotkeysToNavLinks(personLinks, {
      reservedHotkeys: PERSON_PROFILE_SIDEBAR_RESERVED_HOTKEYS
    })
  };
};

// Re-export from features directory for backward compatibility
export {
  usePersonActions,
  getPersonActionButtons,
  type PersonActionButton
} from "@/features/person/hooks/usePersonActions";
